import { AppLauncher } from "@capacitor/app-launcher";
import { Browser } from "@capacitor/browser";
import { GARMIN_ANDROID_PACKAGE, GARMIN_IOS_SCHEME } from "@/lib/garminIntent";
import { isNative, platform } from "./capacitor";

/**
 * Open the Garmin authorize URL from the native shell (#371, #379): the
 * Garmin Connect app first, an in-app browser only when it is not installed.
 *
 * Neither in-app browser hands the URL to another app: a Chrome Custom Tab
 * never re-routes to an App Link, and SFSafariViewController does not act on
 * a universal link for the page it was opened with (it only offers a banner).
 * So when Garmin Connect is present the shell opens the URL with the OS
 * instead - a plain VIEW intent on Android, `UIApplication.open` on iOS - and
 * the OS resolves `connect.garmin.com/oauth2Confirm` to the app, which sends
 * the user back on the `pbranks://` callback. The presence check needs the
 * package declared in <queries> (Android) and the scheme listed under
 * LSApplicationQueriesSchemes (iOS). Without the Garmin app the in-app
 * browser stays, exactly as before.
 */
export async function openAuthorizeUrl(url: string): Promise<void> {
  if (!isNative()) {
    window.location.href = url;
    return;
  }
  const os = platform();
  const probe = os === "android" ? GARMIN_ANDROID_PACKAGE : os === "ios" ? GARMIN_IOS_SCHEME : null;
  if (probe) {
    try {
      const { value } = await AppLauncher.canOpenUrl({ url: probe });
      if (value) {
        const { completed } = await AppLauncher.openUrl({ url });
        if (completed) return;
      }
    } catch {
      /* fall through to the in-app browser */
    }
  }
  await Browser.open({ url, presentationStyle: "popover" });
}
